<script setup lang="ts">
/**
 * A single constellation star "coin" (M4C-26), built from the exact Figma
 * `activity` vectors (Community-management-v2) but coloured with M4C-23 tokens
 * (hex-free). Each state is a circular badge distinguished by *shape and fill*,
 * not colour alone:
 *
 *   earned          — gold coin + a gold star, both with a white outline (embossed)
 *   current/partial — dark coin + a white fragment sparkle (the next target)
 *   locked          — faint, muted coin with a grey star
 *   ghost           — tiny faint dot (decorative background)
 *
 * The vectors are authored in their native Figma viewBoxes (earned: 20×20 with a
 * r=9.5 coin; fragment: 18×18 with a r=9 coin) and placed via a per-coin group
 * transform that scales them to `radius` and centres them at (cx, cy). A
 * `highlighted` star also gets a pulsing ring (just-unlocked emphasis).
 */
import { computed } from 'vue';
import { starStateLabel, type StarRenderState } from '@/composables/gamification/constellation-render';

const props = withDefaults(
  defineProps<{
    /** Projected centre in the renderer's fixed viewBox. */
    cx: number;
    cy: number;
    state: StarRenderState;
    /** Coin radius in viewBox units. */
    radius: number;
    label?: string;
    showLabel?: boolean;
    labelSize?: number;
    highlighted?: boolean;
    animated?: boolean;
  }>(),
  {
    label: '',
    showLabel: false,
    labelSize: 4,
    highlighted: false,
    animated: true,
  },
);

// Exact Figma paths. Earned star lives in a 20×20 box (coin centre 10,10 r 9.5);
// the fragment sparkle in an 18×18 box (coin centre 9,9 r 9).
const EARNED_STAR =
  'M10.498 5.79395L11.4873 7.9834L14.0791 8.1748L15.3623 8.26953L14.3516 9.06641L12.4072 10.5977L12.998 12.875L13.293 14.0107L12.2695 13.4355L10.042 12.1836L7.81445 13.4355L6.79102 14.0107L7.08594 12.875L7.67578 10.5977L5.73242 9.06641L4.72168 8.26953L6.00488 8.1748L8.5957 7.9834L9.58594 5.79395L10.042 4.78613L10.498 5.79395Z';
const FRAGMENT_SPARKLE =
  'M9 15L7.85195 11.7716L4.75736 13.2426L6.22836 10.1481L3 9L6.22836 7.85195L4.75736 4.75736L7.85195 6.22836L9 3L10.1481 6.22836L13.2426 4.75736L11.7716 7.85195L15 9L11.7716 10.1481L13.2426 13.2426L10.148 11.7716L9 15Z';

// Place a native-viewBox vector at (cx,cy) scaled so its coin radius == props.radius.
const earnedTransform = computed(
  () => `translate(${props.cx} ${props.cy}) scale(${props.radius / 9.5}) translate(-10 -10)`,
);
const fragmentTransform = computed(
  () => `translate(${props.cx} ${props.cy}) scale(${props.radius / 9}) translate(-9 -9)`,
);

const labelY = computed(() => props.cy + props.radius + props.labelSize + 2.5);

const titleText = computed(() =>
  props.label ? `${props.label} — ${starStateLabel(props.state)}` : starStateLabel(props.state),
);

const groupClass = computed(() => [
  'gami-star',
  `gami-star--${props.state}`,
  { 'is-animated': props.animated, 'is-highlighted': props.highlighted },
]);
</script>

<template>
  <g :class="groupClass">
    <title>{{ titleText }}</title>

    <!-- Just-unlocked emphasis: a ring (shape) that pulses (motion), never colour-only. -->
    <circle
      v-if="highlighted"
      class="gami-star__pulse fill-none stroke-star"
      :cx="cx"
      :cy="cy"
      :r="radius * 1.55"
      :stroke-width="radius * 0.18"
    />

    <!-- EARNED: gold coin + gold star, both white-outlined (embossed). -->
    <g v-if="state === 'earned'" :transform="earnedTransform">
      <circle cx="10" cy="10" r="9.5" class="fill-star stroke-white" stroke-width="1" />
      <path :d="EARNED_STAR" class="fill-star stroke-white" stroke-width="1" stroke-linejoin="round" />
    </g>

    <!-- CURRENT / PARTIAL: dark coin + white fragment sparkle (the next target). -->
    <g v-else-if="state === 'current' || state === 'partial'" :transform="fragmentTransform">
      <circle cx="9" cy="9" r="9" class="fill-fontColor" />
      <path :d="FRAGMENT_SPARKLE" class="fill-white" />
    </g>

    <!-- LOCKED: faint, muted coin (same shape, desaturated). -->
    <g v-else-if="state === 'locked'" :transform="earnedTransform">
      <circle cx="10" cy="10" r="9.5" class="fill-fontColor-w95 stroke-fontColor-w80" stroke-width="1" />
      <path :d="EARNED_STAR" class="fill-fontColor-w95 stroke-fontColor-w60" stroke-width="1" stroke-linejoin="round" />
    </g>

    <!-- GHOST: faint background dot. -->
    <circle v-else class="gami-star__ghost fill-fontColor-w80" :cx="cx" :cy="cy" :r="radius * 0.34" />

    <text
      v-if="showLabel && label && state !== 'ghost'"
      class="gami-star__label fill-fontColor-w60 font-inter"
      :x="cx"
      :y="labelY"
      :font-size="labelSize"
      text-anchor="middle"
    >
      {{ label }}
    </text>
  </g>
</template>

<style scoped>
.gami-star__ghost {
  opacity: 0.6;
}

/* Static fallback: highlighted ring stays visible even with motion disabled. */
.gami-star__pulse {
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
}
.gami-star.is-highlighted .gami-star__pulse {
  opacity: 0.85;
}
.gami-star.is-animated.is-highlighted .gami-star__pulse {
  animation: gami-pulse 1.8s ease-out infinite;
}

@keyframes gami-pulse {
  0% {
    opacity: 0.85;
    transform: scale(0.7);
  }
  70%,
  100% {
    opacity: 0;
    transform: scale(1.55);
  }
}

@media (prefers-reduced-motion: reduce) {
  .gami-star.is-animated .gami-star__pulse {
    animation: none;
  }
}
</style>
